import React from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {Pencil, Eye, Loader2, LayoutDashboard, Sparkles} from 'lucide-react'
import { useSiteEdit } from '@/contexts/SiteEditContext'

/**
 * Barre flottante visible uniquement pour les administrateurs.
 * Permet de basculer le mode edition en direct sur le site public.
 */
const LiveEditToolbar: React.FC = () => {
  const { canEdit, editMode, setEditMode, saving } = useSiteEdit()

  if (!canEdit) return null

  return (
    <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[200] print:hidden">
      <motion.div
        initial={{ y: 40, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ type: 'spring', stiffness: 260, damping: 24 }}
        className="flex items-center gap-2 bg-[#0A1525]/95 backdrop-blur-xl border border-[#A3C644]/25 rounded-full pl-4 pr-2 py-2 shadow-2xl"
      >
        <span className="flex items-center gap-2 text-[#C9A86A] text-xs tracking-[0.15em] uppercase">
          <Sparkles className="w-3.5 h-3.5" />
          Éditeur
        </span>

        <span className="w-px h-5 bg-[#E8DCC4]/15" />

        <AnimatePresence mode="wait">
          {saving && (
            <motion.span
              key="saving"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              className="flex items-center gap-1.5 text-[#A3C644] text-xs px-2"
            >
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
              Enregistrement…
            </motion.span>
          )}
        </AnimatePresence>

        <button
          onClick={() => setEditMode(!editMode)}
          className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all ${
            editMode
              ? 'bg-[#A3C644] text-[#0E1B2C] hover:bg-[#b4d655]'
              : 'bg-[#E8DCC4]/5 text-[#E8DCC4]/80 hover:bg-[#E8DCC4]/10 border border-[#E8DCC4]/15'
          }`}
        >
          {editMode ? (
            <>
              <Eye className="w-4 h-4" />
              Aperçu
            </>
          ) : (
            <>
              <Pencil className="w-4 h-4" />
              Modifier le site
            </>
          )}
        </button>

        <a
          href="/admin"
          className="flex items-center gap-2 px-3 py-2 rounded-full text-sm text-[#E8DCC4]/60 hover:text-[#E8DCC4] hover:bg-[#E8DCC4]/5 transition-colors"
          title="Tableau de bord"
        >
          <LayoutDashboard className="w-4 h-4" />
        </a>
      </motion.div>

      <AnimatePresence>
        {editMode && (
          <motion.p
            initial={{ opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            className="text-center text-[#E8DCC4]/45 text-[11px] mt-2 tracking-wide"
          >
            Survolez un texte ou une image, puis cliquez pour modifier.
          </motion.p>
        )}
      </AnimatePresence>
    </div>
  )
}

export default LiveEditToolbar
